import _ from "lodash";
import { getAuthToken } from "utils/auth_helpers";
import { BASE_URL } from "utils/constants";

type SubscribeArgs<T> = {
  resourcePath: string;
  fetchLatest: () => Promise<T | null>;
  parseMessage: (data: unknown) => T | null;
  onUpdate: (value: T | null) => void;
  isEqual?: (a: T | null, b: T | null) => boolean;
  pollingMs?: number;
};

type CleanupFn = () => void;

const DEFAULT_POLL_MS = 5000;

/**
 * Problem: When a user perform an action, other players need to update their frontend state.
 * Solution: Subscribe to server-sent events (SSE), where the server pushes updates
 * This allows us to keep browsers up to date without constantly requesting new gamestates (i.e. polling)
 * Example usage: Game state, lobby state (e.g. another player joins)
 *
 * Additional features:
 *  - exponential backoff when SSE doesn't respond (up to 30s)
 *  - reverts to polling in browsers that don't support EventSource (should be rare)
 */
export const subscribeToStream = <T>({
  resourcePath,
  fetchLatest,
  parseMessage,
  onUpdate,
  isEqual = _.isEqual,
  pollingMs = DEFAULT_POLL_MS, // Note: polling is only used if the client doesn't support EventSource, which should be rare.
}: SubscribeArgs<T>): CleanupFn => {
  let eventSource: EventSource | null = null;
  let latest: T | null = null;
  let isMounted = true;
  let reconnectAttempts = 0;
  let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let pollingIntervalId: ReturnType<typeof setInterval> | null = null;

  const updateValue = (value: T | null) => {
    // Only update if there's a change, preventing unnecessary rerenders
    if (latest !== null && value !== null && isEqual(latest, value)) {
      return;
    }
    latest = value;
    onUpdate(value);
  };

  const startPolling = () => {
    if (pollingIntervalId !== null) {
      return;
    }
    fetchLatest().then((value) => {
      if (!isMounted) {
        return;
      }
      updateValue(value);
    });
    pollingIntervalId = setInterval(() => {
      fetchLatest().then((value) => {
        if (!isMounted) return;
        updateValue(value);
      });
    }, pollingMs);
  };

  const stopPolling = () => {
    if (pollingIntervalId !== null) {
      clearInterval(pollingIntervalId);
      pollingIntervalId = null;
    }
  };

  const scheduleReconnect = () => {
    if (!isMounted) {
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
    reconnectAttempts += 1;

    if (reconnectTimeoutId !== null) {
      clearTimeout(reconnectTimeoutId);
    }

    reconnectTimeoutId = setTimeout(() => {
      reconnectTimeoutId = null;
      connect();
    }, delay);
  };

  const connect = () => {
    if (!isMounted) return;
    if (typeof EventSource === "undefined") {
      console.warn("EventSource not supported; falling back to polling.");
      startPolling();
      return;
    }
    if (eventSource) {
      eventSource.close();
    }

    const token = getAuthToken();
    const url = new URL(`${BASE_URL}${resourcePath}`);
    if (token) {
      url.searchParams.set("token", token);
    }
    eventSource = new EventSource(url.toString());

    eventSource.onopen = () => {
      stopPolling();
      reconnectAttempts = 0;
      if (reconnectTimeoutId !== null) {
        clearTimeout(reconnectTimeoutId);
        reconnectTimeoutId = null;
      }
    };

    eventSource.onmessage = (event) => {
      stopPolling();
      try {
        const value = parseMessage(JSON.parse(event.data));
        updateValue(value);
      } catch (err) {
        console.error("Unable to parse SSE payload", err);
      }
    };

    eventSource.onerror = (err) => {
      console.error("Stream error", err);
      eventSource?.close();
      startPolling();
      scheduleReconnect();
    };
  };

  connect();

  const cleanupFn = () => {
    isMounted = false;
    if (eventSource) {
      eventSource.close();
    }
    if (reconnectTimeoutId !== null) {
      clearTimeout(reconnectTimeoutId);
    }
    stopPolling();
  };

  return cleanupFn;
};

export default subscribeToStream;
